import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import xdk from '@accedo/xdk-core';
import { vKey } from '@accedo/xdk-virtual-key';

import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import screenfull from 'screenfull';
import classNames from 'classnames';
import { FocusDiv } from '@accedo/vdkweb-tv-ui';
import { focusManager } from '@accedo/vdkweb-navigation';
import { isEmpty } from 'lodash';
import {
  getVideoFullScreen,
  getCurrentVideo,
  getPlaylistIds,
  getPlayerVideoIndex,
  getPlayerContainerIndex,
  getVideoStarted,
  getPlayerState,
} from '#/redux/modules/appPlayer/selectors';
import { isOpen } from '#/redux/modules/modal/selectors';
import {
  getAssetData,
  getContainerData,
  getSponsoredVideos,
} from '#/redux/modules/brightcove/selectors';
import { sendScreenView } from '#/redux/modules/firestore/actions';
import styles from './playerOverlay.scss';
import playerModalstyles from '#/components/PlayerModal/playerModal.scss';
import PlayerMoreContent from './PlayerMoreContent';
import {
  playerConstants,
  signupStates,
  modalScreenNames,
  GUEST_MODAL_TYPE,
  videoAnalytics,
} from '#/config/constants';
import PlayerCCButton from '#/widgets/ui/Buttons/PlayerCCButton';
import FavoriteButton from '#/widgets/ui/Buttons/FavoriteButton';
import PlayerBackButton from '#/widgets/ui/Buttons/PlayerBackButton';
import PlayerEpisodesButton from '#/widgets/ui/Buttons/PlayerEpisodesButton';
import PlayerFullscreenButton from '#/widgets/ui/Buttons/PlayerFullscreenButton';
import TVPlayerControls from '#/components/Player/TVPlayerControls';

import { setNextView, APP_STATUS } from '#/redux/modules/lifecycle/actions';
import {
  getCurrentView,
  getNextView,
  getViewTransition,
  getDeepLinkStarted,
  getDeeplinkOutsidePlayer,
} from '#/redux/modules/lifecycle/selectors';

import { getCustomDataVideoFavoritesById } from '#/redux/modules/identity/selectors';

import { toggleSpinner } from '#/redux/modules/spinner/actions';

import { isWorkstation, isTizen } from '#/utils/componentStyleConfig';
import {
  updateVideoFavoriteById,
  switchToParentProfile,
} from '#/redux/modules/identity/actions';
import { actions } from '#/redux/modules/appPlayer';
import useToggleHeader from '#/hooks/useToggleHeader';
import useLatest from '#/hooks/useLatest';
import { isGuestMode } from '#/redux/modules/user/selectors';
import {
  getCurrentProfileId,
  getCurrentProfileMinAge,
} from '#/redux/modules/onboarding/selectors';
import { getParentUserId } from '#/redux/modules/identity/identityUser/selectors';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import { actions as onboardingActions } from '#/redux/modules/onboarding/actions';
import { logout } from '#/redux/modules/identity/identityGuest/actions';
import { authFlowRoutes } from '#/utils/routes';

import {
  PLAYER,
  PLAYER_TOP_OVERLAY,
  HOME,
  AGE_UP_MODAL,
} from '#/utils/navigationHelper';
import PlayerHideButton from '../../widgets/ui/Buttons/PlayerHideButton';

// Track switching
import {
  setVideoContent,
  videoStarted,
} from '#/redux/modules/appPlayer/actions';

import assembleCurrentVideo from '#/utils/assembleCurrentVideo';

import playerOverlayBg from '#/static/images/background-images/VideoPlayerBgGradient.png';
import { playerTimeFormatter } from '#/utils/time';
import { useIsInitialDeeplink } from '#/hooks/useIsInitialDeeplink';

// IMPORTANT - the ref to the player was being lost
// and the player controls with them.
// This function will make sure that a ref to the current player is get
const getPlayer = () => xdk.media?.__player?.getPlayerObject?.() || {};

const startHideTimer = (timeRef, hide, customTime = 0) => {
  const time = customTime || playerConstants.HIDE_OVERLAY_TIMEOUT;
  timeRef.current = setTimeout(() => {
    hide();
  }, time);
};

const PlayerOverlay = ({
  videoId,
  history,
  container,
  fullscreenContainer,
  fromHome = false,
  isBackgroundPlayerFullscreen,
  controlsDisabled,
  location,
}) => {
  const isVideoStarted = useSelector(getVideoStarted);
  const currentView = useSelector(getCurrentView);
  const nextView = useSelector(getNextView);
  const viewTransition = useSelector(getViewTransition);
  const playerState = useSelector(getPlayerState);

  const isModalOpen = useSelector(state => isOpen(state));
  const isFullScreen = useSelector(state => getVideoFullScreen(state));
  const currentVideo = useSelector(state => getCurrentVideo(state));
  const { duration, id: assetId, tags = [] } = useSelector(getAssetData) || {};
  const sponsoredVideos = useSelector(getSponsoredVideos) || [];
  const isVideoSponsored = useRef(false);
  useEffect(() => {
    for (let i = 0; i < sponsoredVideos.length; i += 1) {
      if (Number(sponsoredVideos[i]) === currentVideo?.videoId) {
        isVideoSponsored.current = true;
      }
    }
  }, [sponsoredVideos, currentVideo]);

  const videoFavorite = useSelector(state =>
    getCustomDataVideoFavoritesById(state, currentVideo?.videoId),
  );
  const [portalContainer] = useState(document.createElement('div'));
  const hideTimer = useRef();
  const [showContentFilteringModal, setShowContentFilteringModal] = useState(
    false,
  );
  const { CONTAINER, PARENT_INFO_MODAL, WATCH_NOW } = AGE_UP_MODAL;
  // const mouseTimer = useRef();
  // const timerChecker = useRef();
  const overlayContainerRef = useRef();
  const toggleHeader = useToggleHeader();
  const dispatch = useDispatch();
  const lastVideoId = useRef(videoId);
  const seekSpeed = useRef(0);
  const seekTimer = useRef();
  const currentTime = useRef(0);
  const videoChanged = useRef(false);
  const seekStarted = useRef(false);
  const progressBarRef = useRef(null);
  const currentTimeDisplayRef = useRef(null);

  const latestIsPlayerFullscreen = useLatest(isBackgroundPlayerFullscreen);
  const latestFromHome = useLatest(fromHome);
  const latestIsModalOpen = useLatest(isModalOpen);
  const latestControlsDisabled = useLatest(controlsDisabled);

  // Video track switching
  const playlistIds = useSelector(getPlaylistIds);
  const videoItems = useSelector(state => getContainerData(state, playlistIds));
  const videoContainerIndex = useSelector(getPlayerContainerIndex);
  const videoIndex = useSelector(getPlayerVideoIndex);
  const [videoHeaderMetadata, setVideoHeaderMetadata] = useState({
    videoTitle: '',
    videoShowTitle: '',
  });
  const [moreContentItems, setMoreContentItems] = useState({});

  useEffect(() => {
    if (playerState !== videoAnalytics.controls.seeked) {
      return;
    }
    // seek has finished
    seekStarted.current = false;
  }, [playerState]);

  const seekStart = useCallback(() => {
    if (seekStarted.current) {
      return;
    }
    // Dispatch seek start event here as
    // 'seeking' event is unreliable
    seekStarted.current = true;
    dispatch(actions.setVideoSeek());
  }, [seekStarted.current]);

  // ------------------------- CUSTSUPP-21085 ------------------- //
  // Check if the age group of the video match the selected group
  // if not supress the all videos and favorites button
  useIsInitialDeeplink(location);
  const selectedAge = useSelector(getCurrentProfileMinAge);

  const deepLinkStarted = useSelector(state => getDeepLinkStarted(state));

  const deeplinkFromOutside = useSelector(getDeeplinkOutsidePlayer);
  const profileId = useSelector(getCurrentProfileId);
  const parentUserId = useSelector(getParentUserId);
  const deepLinkedAgeMatch = useMemo(() => {
    const {
      location: { pathname },
    } = history;

    if (
      profileId === parentUserId &&
      deepLinkStarted &&
      parentUserId &&
      profileId
    ) {
      return false;
    }
    if (
      pathname.match('/player/')?.input ||
      deepLinkStarted ||
      deeplinkFromOutside
    ) {
      return (
        deepLinkStarted &&
        Number(assetId) === videoId &&
        tags.includes(`${selectedAge}`)
      );
    }
    return true;
  }, [tags, selectedAge, deepLinkStarted, deeplinkFromOutside]);

  // ------------------------------------------------------------- //

  // todo: modify FF/RW behaviour when finalized
  const skip = (time, direction = 'ff') => {
    let seekTime =
      direction === 'ff'
        ? getPlayer()?.currentTime() + time
        : getPlayer()?.currentTime() - time;
    if (seekTime < 0) {
      seekTime = 0;
    }
    seekStart();
    xdk.media?.seek(seekTime);
  };

  // TODO: (rework) the current logic hides everything before show it, eval if it's still necessary
  const showUI = ({ justTitle = false, delay = 0 }) => {
    const opacity = justTitle ? 0 : 1;
    const playerControlsEl = document.getElementsByClassName(
      playerConstants.PLAYER_CONTROLS_CONTAINER_CLASS,
    )[0];
    const ccBtn = document.getElementById(PLAYER_TOP_OVERLAY.CC_BUTTON);
    const contentBlockingBtn = document.getElementById(
      PLAYER_TOP_OVERLAY.CONTENT_BLOCKING_BUTTON,
    );
    const favBtn = deepLinkedAgeMatch
      ? document.getElementById(PLAYER_TOP_OVERLAY.FAV_BUTTON)
      : true;
    const allEpisodes = document.getElementById(
      PLAYER_TOP_OVERLAY.ALL_EPISODES_BUTTON,
    );
    const tc = document.getElementById(PLAYER.TRANSPORT_CONTROLS);
    const moreContent = document.getElementsByClassName(
      styles.contentContainer,
    )[0];
    const vjsCurTime = document.getElementsByClassName(
      playerConstants.PLAYER_CURRENT_TIME_CLASS,
    )[0];
    const vjsProgress = document.getElementsByClassName(
      playerConstants.PLAYER_TIMELINE_CLASS,
    )[0];

    // during page transition (upnext or all episodes) these might not exist but still get called
    if (
      playerControlsEl &&
      ccBtn &&
      contentBlockingBtn &&
      favBtn &&
      allEpisodes &&
      tc &&
      moreContent &&
      vjsCurTime &&
      vjsProgress
    ) {
      // TODO: (rework) eval if this is necessary
      // hide everything before showing
      ccBtn.style.opacity = 0;
      contentBlockingBtn.style.opacity = 0;
      deepLinkedAgeMatch && (favBtn.style.opacity = 0);
      allEpisodes.style.opacity = 0;
      tc.style.opacity = 0;
      moreContent.style.opacity = 0;
      vjsCurTime.style.opacity = 0;
      vjsProgress.style.opacity = 0;
    }

    const anim = () => {
      const _playerControlsEl = document.getElementsByClassName(
        playerConstants.PLAYER_CONTROLS_CONTAINER_CLASS,
      )[0];
      const _ccBtn = document.getElementById(PLAYER_TOP_OVERLAY.CC_BUTTON);
      const _contentBlockingBtn = document.getElementById(
        PLAYER_TOP_OVERLAY.CONTENT_BLOCKING_BUTTON,
      );
      const _favBtn = deepLinkedAgeMatch
        ? document.getElementById(PLAYER_TOP_OVERLAY.FAV_BUTTON)
        : true;
      const _allEpisodes = document.getElementById(
        PLAYER_TOP_OVERLAY.ALL_EPISODES_BUTTON,
      );
      const _tc = document.getElementById(PLAYER.TRANSPORT_CONTROLS);
      const _moreContent = document.getElementsByClassName(
        styles.contentContainer,
      )[0];
      const _vjsCurTime = document.getElementsByClassName(
        playerConstants.PLAYER_CURRENT_TIME_CLASS,
      )[0];
      const _vjsProgress = document.getElementsByClassName(
        playerConstants.PLAYER_TIMELINE_CLASS,
      )[0];

      if (
        _playerControlsEl &&
        _ccBtn &&
        _contentBlockingBtn &&
        _favBtn &&
        _allEpisodes &&
        _tc &&
        _moreContent &&
        _vjsCurTime &&
        _vjsProgress
      ) {
        _playerControlsEl.style.display = 'block';

        _ccBtn.style.opacity = opacity;
        _contentBlockingBtn.style.opacity = opacity;
        deepLinkedAgeMatch && (_favBtn.style.opacity = opacity);
        _allEpisodes.style.opacity = opacity;
        _tc.style.opacity = opacity;
        _moreContent.style.opacity = opacity;
        _vjsCurTime.style.opacity = opacity;
        _vjsProgress.style.opacity = opacity;
        _vjsProgress.style.bottom = isEmpty(moreContentItems)
          ? '15vh'
          : 'calc(23vw + 2vh)';
        _vjsCurTime.style.bottom = isEmpty(moreContentItems)
          ? '11vh'
          : 'calc(20vw + 3vh)';

        overlayContainerRef.current.parentNode.classList.remove(
          styles.sneakPeekContainer,
        );
        _playerControlsEl.classList.remove(styles.portal);
        _playerControlsEl.style.opacity = 1;
        _playerControlsEl.style.cursor = '';
        !justTitle &&
          !latestIsModalOpen.current &&
          focusManager.changeFocus(PLAYER.CONTAINER);
      }

      clearTimeout(hideTimer.current);
      startHideTimer(hideTimer, () => {
        dispatch(
          setNextView({
            nextView: APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF,
            viewTransition: 'smooth',
          }),
        );
      });
    };

    setTimeout(anim, delay);
  };

  const hideUI = ({ anim = false }) => {
    const playerControlsEl = document.getElementsByClassName(
      playerConstants.PLAYER_CONTROLS_CONTAINER_CLASS,
    )[0];

    if (!playerControlsEl) {
      return;
    }

    if (anim) {
      playerControlsEl.style.opacity = 0;
      playerControlsEl.style.cursor = 'none';
      if (playerControlsEl?.style.opacity === 0) {
        focusManager.changeFocus(PLAYER.FOCUS_SINK);
      }
    } else {
      playerControlsEl.style.display = 'none';
    }
  };

  const playPause = (opt = { startHideUITimeout: false }) => {
    const { startHideUITimeout } = opt;
    if (seekTimer.current > 0) {
      seekSpeed.current = 0;
      clearInterval(seekTimer.current);
      seekTimer.current = 0;
      // For some reason if we don't clear the value that we added videojs will just keep
      // appending to it instead of replacing the value.
      const curTimeText = document.getElementsByClassName(
        'vjs-current-time-display',
      )?.[0];
      curTimeText.innerText = '';
      getPlayer()?.currentTime(currentTime.current);
    }
    !getPlayer()?.paused() ? getPlayer()?.pause() : getPlayer()?.play();
    // start new time if comming from the ff/rw or play/pause buttons in player
    if (startHideUITimeout) {
      clearTimeout(hideTimer.current);
      startHideTimer(hideTimer, () => {
        dispatch(
          setNextView({
            nextView: APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF,
            viewTransition: 'smooth',
          }),
        );
      });
    }
  };

  const exitHomeFullscreen = useCallback(() => {
    dispatch(setNextView({ nextView: APP_STATUS.NAVIGATION_BG_PLAYER_ON }));
    dispatch(actions.setBackgroundPlayerFullscreen(false));
    dispatch(actions.setVideoFullscreenBookmark());
    toggleHeader(true);
    focusManager.changeFocus(HOME.PAGE);
  }, [toggleHeader]);

  // TV back button handler
  const handleBack = useCallback(
    evt => {
      if (evt.id !== 'device:vkey:back') {
        return;
      }
      const playerModalContainer = document
        .getElementsByClassName(playerModalstyles?.container)[0]
        ?.firstElementChild?.classList?.contains(
          playerModalstyles?.backgroundPlayer,
        );
      if (!latestIsPlayerFullscreen.current && !playerModalContainer) {
        return;
      }
      const isPlayerFullscreen = latestIsPlayerFullscreen.current;

      if (latestIsModalOpen.current) {
        return;
      }
      if (isPlayerFullscreen) {
        dispatch(setNextView({ nextView: APP_STATUS.NAVIGATION_BG_PLAYER_ON }));
      }

      if (screenfull.isFullscreen) {
        screenfull.exit();
        return;
      }
      dispatch(actions.setVideoTime(xdk.media?.getCurrentTime()));

      if (latestFromHome.current && isPlayerFullscreen) {
        exitHomeFullscreen();
      } else if (!deepLinkStarted) {
        dispatch(actions.exitPlayback(history));
      }

      setTimeout(() => {
        dispatch(actions.setExitFromPlayer(false));
      }, 100);
    },
    [exitHomeFullscreen, deepLinkStarted],
  );

  const areControlsVisible = () => {
    const playerControlsEl = document.getElementsByClassName(
      playerConstants.PLAYER_CONTROLS_CONTAINER_CLASS,
    )[0];

    return (
      playerControlsEl?.style.opacity !== '0' &&
      playerControlsEl?.style.display !== 'none'
    );
  };

  // hide player control and progress bar when playing sponsored video
  useEffect(() => {
    if (!currentVideo) {
      return;
    }
    const tvControls = document.getElementById(PLAYER.TRANSPORT_CONTROLS);
    const playerProgressControl = document.getElementsByClassName(
      'vjs-progress-control vjs-control',
    )[0];
    if (isVideoSponsored.current && playerProgressControl && tvControls) {
      playerProgressControl.style.display = 'none';
      tvControls.style.display = 'none';
    } else if (playerProgressControl && tvControls) {
      playerProgressControl.style.display = 'block';
      tvControls.style.display = 'flex';
    }
  }, [currentVideo, isVideoSponsored]);

  useEffect(() => {
    const tempFocus = focusManager.getCurrentFocus();
    if (
      history?.location?.pathname === '/main/home' &&
      (tempFocus === CONTAINER ||
        tempFocus === PARENT_INFO_MODAL ||
        tempFocus === WATCH_NOW) &&
      !latestIsModalOpen.current
    ) {
      if (
        focusManager.getCurrentFocus() === PLAYER.FOCUS_SINK &&
        areControlsVisible()
      ) {
        focusManager.changeFocus(PLAYER.CONTAINER);
      } else {
        focusManager.changeFocus(HOME.PAGE);
      }
    }
  }, [focusManager.getCurrentFocus()]);

  // effect for portal into native player controls on mount and dismount
  useEffect(() => {
    container.prepend(portalContainer);
    return () => {
      container.removeChild(portalContainer);
    };
  }, [container]);

  useEffect(() => {
    // update status
    if (lastVideoId.current !== videoId) {
      // TODO: (rework) eval if place this in another place
      dispatch(videoStarted(false));
      // new video, let's transit to a new status, title only on
      dispatch(
        setNextView({ nextView: APP_STATUS.PLAYER_TITLE_ON_CONTROLS_OFF }),
      );
      videoChanged.current = true;
    }
    lastVideoId.current = videoId;

    return () => {
      seekSpeed.current = 0;
      clearInterval(seekTimer.current);
      seekTimer.current = 0;
    };
  }, [videoId]);

  useEffect(() => {
    // console.log(
    //   '----> currentView ',
    //   currentView,
    //   ' - nextView ',
    //   nextView,
    //   ' - viewTransition ',
    //   viewTransition,
    // );
    switch (nextView) {
      case APP_STATUS.NAVIGATION_BG_PLAYER_ON:
        // needed because it is the main responsible for clear
        // the timer when the user leaves the fullscreen mode
        clearTimeout(hideTimer.current);
        hideUI({ anim: false });
        break;
      case APP_STATUS.NAVIGATION_BG_PLAYER_OFF:
        // TODO: (rework) handle this case?
        break;
      case APP_STATUS.PLAYER_TITLE_ON_CONTROLS_ON:
        showUI({ justTitle: false });
        focusManager.changeFocus(
          isEmpty(moreContentItems)
            ? PLAYER.TRANSPORT_CONTROLS
            : PLAYER.CONTAINER,
        );
        break;
      case APP_STATUS.PLAYER_TITLE_ON_CONTROLS_OFF:
        // important: this case will be entered twice
        // first time it will just hide the ui (immediately after a new title is picked from up next swimlane)
        // the second time, when the video is loaded (isVideoStarted) it will also show the title
        // delay to close any open modal before going fullscreen
        // (latestIsModalOpen.current || isModalOpen) &&
        //   setTimeout(() => {
        //     dispatch(modalActions.closeModal());
        //   }, 5000);
        hideUI({ anim: false });
        if (isVideoStarted) {
          showUI({ justTitle: true, delay: 100 });
        }
        break;
      case APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF:
        hideUI({ anim: viewTransition !== '' });
        break;
      default:
        break;
    }
  }, [currentView, nextView, viewTransition, isVideoStarted]);

  useEffect(() => {
    setVideoHeaderMetadata(() => ({
      videoTitle: currentVideo.videoTitle,
      videoShowTitle: currentVideo.videoShowTitle,
    }));
  }, [currentVideo]);

  useEffect(() => {
    xdk.environment.addEventListener(
      xdk.environment.SYSTEM.KEYDOWN,
      handleBack,
    );
    return () => {
      xdk.environment.removeEventListener(
        xdk.environment.SYSTEM.KEYDOWN,
        handleBack,
      );
    };
  }, []);

  // effect for hide mouse
  useEffect(() => {
    const onMouseMove = payload => {
      if (!isBackgroundPlayerFullscreen || videoChanged.current) {
        videoChanged.current = false;
        return;
      }
      const keyId = payload?.id;
      const isRewindKey = [
        vKey.RW.id,
        vKey.LEFT.id,
        'tizen:vkey:mediarewind',
        'device:vkey:mediarewind',
      ].includes(keyId);
      const isFastForwardKey = [
        vKey.FF.id,
        vKey.RIGHT.id,
        'tizen:vkey:mediafastforward',
        'device:vkey:mediafastforward',
      ].includes(keyId);
      const isPlayPauseKey = [
        vKey.PLAY_PAUSE.id,
        'tizen:vkey:playpause',
        'device:vkey:playpause',
      ].includes(keyId);

      // handle:
      // - play pause key on the remote
      // - left and right buttons make video jump 10 seconds when controls are hidden jump - cert requirement (only Tizen)
      // - channel up / down shouldn't perform any action - cert requirement (only Tizen)
      if (isPlayPauseKey) {
        playPause();
      } else if (isRewindKey) {
        if (isTizen() && !latestControlsDisabled.current) {
          // Certification expects dedicated trick-play keys to work even
          // when transport controls are hidden.
          skip(10, 'rw');
          return;
        }
      } else if (isFastForwardKey) {
        if (isTizen() && !latestControlsDisabled.current) {
          // Certification expects dedicated trick-play keys to work even
          // when transport controls are hidden.
          skip(10, 'ff');
          return;
        }
      } else if (keyId === 'tizen:vkey:chup' || keyId === 'tizen:vkey:chdown') {
        return;
      } else if (
        focusManager.getCurrentFocus() === PLAYER.FOCUS_SINK &&
        areControlsVisible()
      ) {
        focusManager.changeFocus(PLAYER.CONTAINER);
      }

      if (nextView === APP_STATUS.NAVIGATION_BG_PLAYER_ON) {
        // TODO: (rework) eval if this logic (first conditional only) should be somewhere else
        // this case happens when the user is at home page and clicks on the same title he is watching
        // maybe we should move this logic in the ontileclick function
        dispatch(
          setNextView({ nextView: APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF }),
        );
      } else if (
        nextView === APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF ||
        nextView === APP_STATUS.PLAYER_TITLE_ON_CONTROLS_OFF
      ) {
        // this case it's for normal re-activation of UI while the user is watching
        dispatch(
          setNextView({ nextView: APP_STATUS.PLAYER_TITLE_ON_CONTROLS_ON }),
        );
      }

      // needed because we need to procrastinate the timeout just after the user pressed a button
      clearTimeout(hideTimer.current);
      startHideTimer(hideTimer, () => {
        dispatch(
          setNextView({
            nextView: APP_STATUS.PLAYER_TITLE_OFF_CONTROLS_OFF,
            viewTransition: 'smooth',
          }),
        );
      });
    };

    const onMouseClick = evt => {
      if (
        parseInt(container.style.opacity, 10) === 1 &&
        evt.target === overlayContainerRef?.current
      ) {
        container.style.opacity = 0;
        container.style.cursor = 'none';
      } else {
        onMouseMove();
      }
    };

    if (!isModalOpen && isBackgroundPlayerFullscreen) {
      if (!isWorkstation()) {
        xdk.environment.addEventListener(
          xdk.environment.SYSTEM.KEYDOWN,
          onMouseMove,
        );
      }
    } else {
      // TODO: (rework) needed?
      clearTimeout(hideTimer.current);
      if (!isWorkstation()) {
        xdk.environment.removeEventListener(
          xdk.environment.SYSTEM.KEYDOWN,
          onMouseMove,
        );
      }
    }

    const playerControlsEl = document.getElementsByClassName(
      playerConstants.PLAYER_CONTROLS_CONTAINER_CLASS,
    )[0];

    playerControlsEl?.parentElement?.parentElement?.parentElement?.addEventListener(
      'mousemove',
      onMouseMove,
    );
    playerControlsEl?.parentElement?.parentElement?.parentElement?.addEventListener(
      'click',
      onMouseClick,
    );
    playerControlsEl?.classList.add(styles.portal);

    return () => {
      // TODO: (rework) needed?
      // (apparently) needed because otherwise some focus lost happens after the user goes back to home
      clearTimeout(hideTimer.current);
      // TODO: is this necessary? - it seems to be tricky inside a clean up function...
      container.style.opacity = 1;
      container.style.cursor = '';

      playerControlsEl?.parentElement?.parentElement?.parentElement?.removeEventListener(
        'mousemove',
        onMouseMove,
      );
      playerControlsEl?.parentElement?.parentElement?.parentElement?.removeEventListener(
        'click',
        onMouseClick,
      );
      if (!isWorkstation()) {
        xdk.environment.removeEventListener(
          xdk.environment.SYSTEM.KEYDOWN,
          onMouseMove,
        );
      }
    };
  }, [isModalOpen, isBackgroundPlayerFullscreen, currentView, nextView]);

  useEffect(() => {
    if (showContentFilteringModal) {
      dispatch(
        modalActions.openModal({
          modalId: 'ContentFilteringModal',
        }),
      );
    }
  }, [showContentFilteringModal]);

  const guestMode = useSelector(isGuestMode);
  const onToggleVideoFavorite = useCallback(async () => {
    if (guestMode) {
      if (screenfull.isFullscreen) {
        screenfull.exit();
      }
      !getPlayer()?.paused() && playPause();
      dispatch(
        modalActions.openModal({
          modalId: 'GuestProfileModal',
          modalData: {
            modalType: GUEST_MODAL_TYPE.favoritesBlocked,
          },
        }),
      );
    } else {
      requestAnimationFrame(() => {
        dispatch(toggleSpinner(true));
      });
      const videoData = {
        id: `${currentVideo.videoId}`,
        name: currentVideo.videoTitle,
        reference_id: currentVideo.videoReferenceId,
        thumbnail: currentVideo.thumbnail,
        custom_fields: {
          subtitle: currentVideo.videoShowTitle,
          episode_key_art_horizontal: currentVideo.episode_key_art_horizontal,
        },
        duration,
      };
      await dispatch(
        updateVideoFavoriteById(
          currentVideo.videoId,
          videoFavorite === null,
          videoData,
        ),
      );
      requestAnimationFrame(() => {
        dispatch(toggleSpinner(false));
      });
    }
  }, [currentVideo, videoFavorite, duration, guestMode]);

  const onToggleHideContent = useCallback(async () => {
    if (guestMode) {
      if (screenfull.isFullscreen) {
        screenfull.exit();
      }
      !getPlayer()?.paused() && playPause();
      dispatch(
        modalActions.openModal({
          modalId: 'GuestProfileModal',
          modalData: {
            modalType: GUEST_MODAL_TYPE.contentBlockingBlocked,
          },
          successAction: () => {
            dispatch(
              onboardingActions.setSignupState({
                signupState: signupStates.CREATE_ACCOUNT,
              }),
            );
            dispatch(logout(authFlowRoutes.ACCOUNT_CREATION));
          },
        }),
      );
    } else {
      requestAnimationFrame(() => {
        dispatch(toggleSpinner(true));
      });
      if (screenfull.isFullscreen) {
        screenfull.exit();
      }
      await dispatch(
        modalActions.openModal({
          modalId: 'PinModal',
          modalData: {
            isNewAccountMobile: false,
          },
          successAction: async pin => {
            await dispatch(
              switchToParentProfile({ credential: pin, blocking: true }),
            );
            setShowContentFilteringModal(true);
          },
        }),
      );
      requestAnimationFrame(() => {
        dispatch(toggleSpinner(false));
      });
    }
    dispatch(
      sendScreenView(
        `${modalScreenNames.ContentBlockingModal} (${currentVideo.videoId})`,
      ),
    );
  }, [guestMode]);

  const changeTrack = useCallback(
    offset => {
      const currentPlaylistIndex = playlistIds[videoContainerIndex];
      const upNextVideoItem =
        videoItems[currentPlaylistIndex]?.[videoIndex + offset] || null;
      if (!upNextVideoItem) {
        return;
      }

      dispatch(
        setVideoContent({
          playlists: [...playlistIds],
          currentPlaylistIndex: videoContainerIndex,
          currentVideoIndex: videoIndex + offset,
          currentVideo: assembleCurrentVideo(upNextVideoItem),
          initiation: videoAnalytics.initiation.AUTO,
        }),
      );
    },
    [playlistIds, videoContainerIndex, videoIndex, videoItems],
  );

  const onRWClick = useCallback(() => {
    if (!getPlayer()?.paused()) {
      getPlayer()?.pause();
      currentTime.current = getPlayer()?.currentTime();
    }

    let step = 0;

    if (seekSpeed.current >= 0) {
      seekSpeed.current = -1;
      step = 5; // only first step should be 5 sec
    } else {
      seekSpeed.current = Math.max(seekSpeed.current * 2, -16);
      step = 10;
    }

    seekStart();
    clearInterval(seekTimer.current);
    if (!currentTimeDisplayRef.current) {
      currentTimeDisplayRef.current = document.getElementsByClassName(
        'vjs-current-time-display',
      )?.[0];
    }
    if (!progressBarRef.current) {
      progressBarRef.current = document.getElementsByClassName(
        'vjs-play-progress',
      )?.[0];
    }
    seekTimer.current = setInterval(() => {
      // needed in order to avoid hide ui while rwing
      clearTimeout(hideTimer.current);
      const curTimeText = currentTimeDisplayRef.current;
      const progBar = progressBarRef.current;
      currentTime.current += step * seekSpeed.current;
      currentTime.current = Math.max(0, currentTime.current);
      const width = Math.max(
        0,
        (currentTime.current / getPlayer()?.duration()) * 100,
      );
      if (progBar) {
        progBar.style.width = `${width}%`;
      }
      if (curTimeText) {
        curTimeText.innerText = playerTimeFormatter(
          currentTime.current,
          currentTime.current,
        );
      }

      // reached the end, start playback so we skip to the next video
      if (width <= 0) {
        getPlayer()?.currentTime(currentTime.current);
        playPause({ startHideUITimeout: true });
      }
    }, 1000);
  }, []);

  const onFFClick = useCallback(() => {
    if (!getPlayer()?.paused()) {
      getPlayer()?.pause();
      currentTime.current = getPlayer()?.currentTime();
    }

    let step = 0;

    if (seekSpeed.current <= 0) {
      seekSpeed.current = 1;
      step = 5; // only first step should be 5 sec
    } else {
      seekSpeed.current = Math.min(seekSpeed.current * 2, 16);
      step = 10;
    }

    seekStart();
    clearInterval(seekTimer.current);
    if (!currentTimeDisplayRef.current) {
      currentTimeDisplayRef.current = document.getElementsByClassName(
        'vjs-current-time-display',
      )?.[0];
    }
    if (!progressBarRef.current) {
      progressBarRef.current = document.getElementsByClassName(
        'vjs-play-progress',
      )?.[0];
    }
    seekTimer.current = setInterval(() => {
      // needed in order to avoid hide ui while ffing
      clearTimeout(hideTimer.current);
      const curTimeText = currentTimeDisplayRef.current;
      const progBar = progressBarRef.current;
      currentTime.current += step * seekSpeed.current;
      currentTime.current = Math.min(
        currentTime.current,
        getPlayer()?.duration(),
      );
      const width = Math.min(
        100,
        (currentTime.current / getPlayer()?.duration()) * 100,
      );
      if (progBar) {
        progBar.style.width = `${width}%`;
      }
      if (curTimeText) {
        curTimeText.innerText = playerTimeFormatter(
          currentTime.current,
          currentTime.current,
        );
      }

      // reached the end, start playback just before the video end so we skip to the next video
      if (width >= 100) {
        changeTrack(1);
      }
    }, 700);
  }, [changeTrack]);

  const onPrevTrack = useCallback(() => {
    changeTrack(-1);
  }, [changeTrack]);

  const onNextTrack = useCallback(() => {
    changeTrack(1);
  }, [changeTrack]);

  useEffect(() => {
    // Reset cached DOM nodes when view/player tree changes.
    currentTimeDisplayRef.current = null;
    progressBarRef.current = null;
  }, [videoId, isFullScreen, currentView, nextView]);

  return ReactDOM.createPortal(
    <FocusDiv
      className={styles.sneakPeekContainer}
      nav={{
        id: PLAYER.CONTAINER,
        forwardFocus: isEmpty(moreContentItems)
          ? PLAYER.PLAY_PAUSE
          : PLAYER.CONTROL,
      }}
    >
      <div
        ref={overlayContainerRef}
        style={{
          display: isFullScreen ? 'block' : 'none',
          backgroundImage: isWorkstation()
            ? 'linear-gradient(0deg, rgba(0, 0, 0, 1) 15%, rgba(0, 0, 0, .78) 30%, transparent 44%)'
            : `url(${playerOverlayBg})`,
        }}
        className={classNames(styles.container, {
          [styles.isTv]: !isWorkstation(),
        })}
      >
        <FocusDiv
          className={styles.topContainer}
          nav={{
            id: PLAYER_TOP_OVERLAY.CONTAINER,
            forwardFocus: PLAYER_TOP_OVERLAY.CC_BUTTON,
            useLastFocus: true,
            parent: PLAYER.CONTAINER,
          }}
        >
          {isWorkstation() && <PlayerBackButton setTime />}
          <div className={styles.metaContainer}>
            <h2
              dangerouslySetInnerHTML={{
                __html: videoHeaderMetadata.videoTitle,
              }}
            />
            <h5>{videoHeaderMetadata.videoShowTitle}</h5>
          </div>

          <PlayerCCButton
            nav={{
              id: PLAYER_TOP_OVERLAY.CC_BUTTON,
              nextright: deepLinkedAgeMatch
                ? PLAYER_TOP_OVERLAY.CONTENT_BLOCKING_BUTTON
                : PLAYER_TOP_OVERLAY.ALL_EPISODES_BUTTON,
              nextdown: PLAYER.TRANSPORT_CONTROLS,
              parent: PLAYER_TOP_OVERLAY.CONTAINER,
            }}
          />
          {!isVideoSponsored.current && (
            <PlayerHideButton
              containerClassName={!deepLinkedAgeMatch ? styles.hideAway : ''}
              nav={{
                id: PLAYER_TOP_OVERLAY.CONTENT_BLOCKING_BUTTON,
                nextleft: PLAYER_TOP_OVERLAY.CC_BUTTON,
                nextright: PLAYER_TOP_OVERLAY.FAV_BUTTON,
                nextdown: PLAYER.TRANSPORT_CONTROLS,
                parent: PLAYER_TOP_OVERLAY.CONTAINER,
              }}
              onClick={onToggleHideContent}
            />
          )}
          {(deepLinkedAgeMatch || guestMode) && !isVideoSponsored.current && (
            <FavoriteButton
              containerClassName={!deepLinkedAgeMatch ? styles.hideAway : ''}
              usePlayerStyle
              isFavorited={videoFavorite !== null}
              nav={{
                id: PLAYER_TOP_OVERLAY.FAV_BUTTON,
                nextleft: PLAYER_TOP_OVERLAY.CONTENT_BLOCKING_BUTTON,
                nextright: PLAYER_TOP_OVERLAY.ALL_EPISODES_BUTTON,
                nextdown: PLAYER.TRANSPORT_CONTROLS,
                parent: PLAYER_TOP_OVERLAY.CONTAINER,
              }}
              onClick={onToggleVideoFavorite}
            />
          )}

          <PlayerEpisodesButton
            fromHome={fromHome}
            nav={{
              id: PLAYER_TOP_OVERLAY.ALL_EPISODES_BUTTON,
              nextleft: deepLinkedAgeMatch
                ? PLAYER_TOP_OVERLAY.FAV_BUTTON
                : PLAYER_TOP_OVERLAY.CC_BUTTON,
              nextdown: PLAYER.TRANSPORT_CONTROLS,
              parent: PLAYER_TOP_OVERLAY.CONTAINER,
            }}
          />
        </FocusDiv>
        {// TODO: didn't get to the root cause of where the player is getting
        // disposed to fix this in a better way so for now this will have to do.
        // Update: added a log where the player (vjs) is disposed in src/players/web/videojs.js
        !isWorkstation() && getPlayer()?.el_ && (
          <TVPlayerControls
            onRWClick={onRWClick} // ={() => skip(10, 'rw')}
            onFFClick={onFFClick} // ={() => skip(10, 'ff')}
            onPrevTrack={onPrevTrack}
            onNextTract={onNextTrack}
            onPlayPauseClick={() => playPause({ startHideUITimeout: true })}
            isVideoPlaying={!getPlayer()?.paused()}
            controlsDisabled={controlsDisabled}
            seekSpeed={seekSpeed.current}
            moreContentItems={moreContentItems}
          />
        )}
        <div className={styles.contentContainer}>
          <PlayerMoreContent
            fromHome={fromHome}
            onSwimlaneDataChange={setMoreContentItems}
          />
        </div>
      </div>
      {screenfull.isEnabled && isWorkstation() && (
        <div>
          <PlayerFullscreenButton
            fullscreenLayout={isFullScreen}
            container={fullscreenContainer}
          />
        </div>
      )}
      <FocusDiv nav={{ id: PLAYER.FOCUS_SINK }} />
    </FocusDiv>,
    portalContainer,
  );
};

PlayerOverlay.propTypes = {
  container: PropTypes.object,
  fullscreenContainer: PropTypes.object,
  fromHome: PropTypes.bool,
  isBackgroundPlayerFullscreen: PropTypes.bool,
  controlsDisabled: PropTypes.bool,
  location: PropTypes.object,
};

export default withRouter(PlayerOverlay);
